import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiShoppingCart, FiCreditCard, FiArrowLeft, FiCheck, FiAlertCircle,
  FiHome, FiPackage, FiTruck, FiShield, FiShare2, FiX, FiBox
} from 'react-icons/fi';
import { shedService } from '../services/shedService';
import { useCart } from '../context/CartContext';
import StripeCheckout from '../components/StripeCheckout';

const ShedCheckout = () => {
  const { shedId } = useParams();
  const { addToCart, isInCart } = useCart();
  const [shed, setShed] = useState(null);
  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', state: '', zip: ''
  });
  const [step, setStep] = useState('details');

  const isSold = shed?.status?.toLowerCase() === 'sold';

  useEffect(() => {
    loadShed();
  }, [shedId]);

  useEffect(() => {
    if (shed && isSold) {
      const timer = setTimeout(() => setShowSoldModal(true), 800);
      return () => clearTimeout(timer);
    }
  }, [shed, isSold]);

  const loadShed = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await shedService.getShedById(shedId);
      if (fetchError || !data) {
        setError('Shed not found');
        return;
      }
      setShed(data);

      if (data.dealerId || data.dealer_id) {
        try {
          const { data: dealerData } = await shedService.getDealerById?.(data.dealerId || data.dealer_id) || {};
          if (dealerData) setDealer(dealerData);
        } catch { /* dealer fetch optional */ }
      }
    } catch (err) {
      console.error('Failed to load shed:', err);
      setError('Failed to load shed details');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0
    }).format(price);
  };

  const effectivePrice = shed?.salePrice && shed.salePrice < shed.price ? shed.salePrice : shed?.price;
  const depositAmount = shed?.depositAmount || Math.round((effectivePrice || 0) * 0.25);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();
    if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.email || !customerInfo.phone) {
      alert('Please fill in all required fields.');
      return;
    }
    setShowPaymentModal(true);
  };

  const buildConfiguratorUrl = () => {
    if (!shed) return '/sheds';
    const params = new URLSearchParams();
    if (shed.shedType) params.set('type', shed.shedType);
    if (shed.size) params.set('size', shed.size);
    if (shed.width) params.set('width', shed.width);
    if (shed.depth) params.set('depth', shed.depth);
    if (shed.height) params.set('height', shed.height);
    if (shed.material) params.set('material', shed.material);
    if (shed.roofStyle) params.set('roofStyle', shed.roofStyle);
    if (shed.roofMaterial) params.set('roofMaterial', shed.roofMaterial);
    if (shed.sidingType) params.set('sidingType', shed.sidingType);
    if (shed.bodyColor) params.set('bodyColor', shed.bodyColor);
    if (shed.trimColor) params.set('trimColor', shed.trimColor);
    if (shed.roofColor) params.set('roofColor', shed.roofColor);
    if (shed.floorType) params.set('floorType', shed.floorType);
    if (shed.doorType) params.set('doorType', shed.doorType);
    if (shed.windowType) params.set('windowType', shed.windowType);
    if (shed.loft) params.set('loft', 'true');
    if (shed.workbench) params.set('workbench', 'true');
    if (shed.electrical) params.set('electrical', 'true');
    if (shed.insulation) params.set('insulation', 'true');
    if (shed.hasRamp) params.set('ramp', 'true');
    if (shed.hasShelving) params.set('shelving', 'true');
    return `/configure?${params.toString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading shed details...</p>
        </div>
      </div>
    );
  }

  if (error || !shed) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 flex items-center justify-center">
        <div className="text-center max-w-md">
          <FiAlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Shed Not Found</h1>
          <p className="text-gray-600 mb-6">This shed may have been removed from our inventory.</p>
          <Link to="/sheds" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
            <FiPackage className="w-5 h-5" />
            Browse Available Sheds
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{shed.name} - {isSold ? 'Sold' : 'Buy Now'} | ShedX</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-20 pb-12">
        {/* Back link */}
        <div className="container mx-auto px-4 py-4">
          <Link to="/sheds" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-emerald-600 transition-colors">
            <FiArrowLeft className="w-4 h-4" /> Back to inventory
          </Link>
        </div>

        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

              {/* Left: Images */}
              <div className="space-y-4">
                <div className="relative aspect-[4/3] bg-gray-100 rounded-xl overflow-hidden">
                  {shed.images?.[selectedImage] ? (
                    <img
                      src={shed.images[selectedImage]}
                      alt={shed.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FiHome className="w-16 h-16 text-gray-300" />
                    </div>
                  )}

                  {/* SOLD watermark */}
                  {isSold && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="bg-red-600/90 text-white text-6xl md:text-8xl font-black tracking-widest px-12 py-6 -rotate-12 shadow-2xl border-4 border-white/30 select-none">
                        SOLD
                      </div>
                    </div>
                  )}
                </div>

                {/* Thumbnails */}
                {shed.images?.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {shed.images.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedImage(i)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                          selectedImage === i ? 'border-emerald-500' : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Right: Details + Purchase */}
              <div className="space-y-6">
                {/* Status badge */}
                {isSold ? (
                  <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-700 text-sm font-semibold px-3 py-1.5 rounded-full">
                    <span className="w-2 h-2 bg-red-500 rounded-full" /> Sold
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-sm font-semibold px-3 py-1.5 rounded-full">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full" /> Available
                  </span>
                )}

                <h1 className="text-3xl font-bold text-gray-900">{shed.name}</h1>

                {/* Specs grid */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {shed.size && (
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <span className="text-gray-500 block text-xs">Size</span>
                      <span className="font-semibold text-gray-900">{shed.size}</span>
                    </div>
                  )}
                  {shed.shedType && (
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <span className="text-gray-500 block text-xs">Style</span>
                      <span className="font-semibold text-gray-900 capitalize">{shed.shedType}</span>
                    </div>
                  )}
                  {shed.material && (
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <span className="text-gray-500 block text-xs">Material</span>
                      <span className="font-semibold text-gray-900 capitalize">{shed.material}</span>
                    </div>
                  )}
                  {shed.roofStyle && (
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <span className="text-gray-500 block text-xs">Roof</span>
                      <span className="font-semibold text-gray-900 capitalize">{shed.roofStyle}</span>
                    </div>
                  )}
                  {shed.serialNumber && (
                    <div className="bg-white rounded-lg p-3 border border-gray-100 col-span-2">
                      <span className="text-gray-500 block text-xs">Serial Number</span>
                      <span className="font-semibold text-gray-900 font-mono">{shed.serialNumber}</span>
                    </div>
                  )}
                </div>

                {/* Color specs */}
                {(shed.bodyColor || shed.trimColor || shed.roofColor) && (
                  <div className="bg-white rounded-lg p-4 border border-gray-100 space-y-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Colors</span>
                    <div className="flex flex-wrap gap-3 text-sm">
                      {shed.bodyColor && (
                        <span className="flex items-center gap-1.5">
                          <span className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: shed.bodyColor }} />
                          Body: {shed.bodyColor}
                        </span>
                      )}
                      {shed.trimColor && (
                        <span className="flex items-center gap-1.5">
                          <span className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: shed.trimColor }} />
                          Trim: {shed.trimColor}
                        </span>
                      )}
                      {shed.roofColor && (
                        <span className="flex items-center gap-1.5">
                          <span className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: shed.roofColor }} />
                          Roof: {shed.roofColor}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Features */}
                {(shed.loft || shed.workbench || shed.electrical || shed.insulation || shed.hasRamp || shed.hasShelving) && (
                  <div className="flex flex-wrap gap-2">
                    {shed.loft && <span className="bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full">Loft</span>}
                    {shed.workbench && <span className="bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full">Workbench</span>}
                    {shed.electrical && <span className="bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full">Electrical</span>}
                    {shed.insulation && <span className="bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full">Insulation</span>}
                    {shed.hasRamp && <span className="bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full">Ramp</span>}
                    {shed.hasShelving && <span className="bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full">Shelving</span>}
                  </div>
                )}

                {/* Price */}
                <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                  {!isSold ? (
                    <>
                      <div className="flex items-baseline gap-3 mb-1">
                        {shed.salePrice && shed.salePrice < shed.price ? (
                          <>
                            <span className="text-3xl font-bold text-emerald-600">{formatPrice(shed.salePrice)}</span>
                            <span className="text-lg text-gray-400 line-through">{formatPrice(shed.price)}</span>
                          </>
                        ) : (
                          <span className="text-3xl font-bold text-gray-900">{formatPrice(shed.price)}</span>
                        )}
                      </div>
                      <div className="mt-3 bg-emerald-50 rounded-lg p-3 flex items-center justify-between">
                        <span className="text-sm font-medium text-emerald-800">Reserve with 25% deposit</span>
                        <span className="text-lg font-bold text-emerald-700">{formatPrice(depositAmount)}</span>
                      </div>

                      {/* CTA buttons */}
                      <div className="mt-5 space-y-3">
                        <button
                          onClick={() => setStep('checkout')}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 text-lg"
                        >
                          <FiCreditCard className="w-5 h-5" />
                          Buy Now — {formatPrice(depositAmount)} Deposit
                        </button>
                        {!isInCart(shed.id) && (
                          <button
                            onClick={() => addToCart(shed)}
                            className="w-full border border-gray-300 hover:border-gray-400 text-gray-700 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                          >
                            <FiShoppingCart className="w-5 h-5" />
                            Add to Cart
                          </button>
                        )}
                      </div>

                      {/* Trust signals */}
                      <div className="mt-5 grid grid-cols-3 gap-2 text-center text-xs text-gray-500">
                        <div className="flex flex-col items-center gap-1">
                          <FiTruck className="w-4 h-4" />
                          <span>Free delivery</span>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <FiShield className="w-4 h-4" />
                          <span>Warranty included</span>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <FiPackage className="w-4 h-4" />
                          <span>Pro setup</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-center py-2">
                        <p className="text-lg font-semibold text-gray-900 mb-1">This shed has been sold</p>
                        <p className="text-sm text-gray-500 mb-4">
                          {shed.salePrice && shed.salePrice < shed.price
                            ? <span>Was listed at <span className="font-medium">{formatPrice(shed.salePrice)}</span></span>
                            : <span>Was listed at <span className="font-medium">{formatPrice(shed.price)}</span></span>
                          }
                        </p>
                        <Link
                          to={buildConfiguratorUrl()}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 text-lg"
                        >
                          <FiBox className="w-5 h-5" />
                          Order One Just Like It
                        </Link>
                        <Link
                          to="/sheds"
                          className="block mt-3 text-sm text-gray-500 hover:text-emerald-600 transition-colors"
                        >
                          or browse available inventory
                        </Link>
                      </div>
                    </>
                  )}
                </div>

                {/* Dealer info */}
                {(shed.dealerName || dealer?.company_name) && (
                  <div className="bg-white rounded-lg p-4 border border-gray-100 flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <FiHome className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{dealer?.company_name || shed.dealerName}</p>
                      {shed.dealerLocation && <p className="text-xs text-gray-500">{shed.dealerLocation}</p>}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Inline checkout form (when step === 'checkout') */}
            <AnimatePresence>
              {step === 'checkout' && !isSold && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="mt-10 max-w-2xl mx-auto"
                  id="checkout-form"
                >
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold text-gray-900">Complete Your Purchase</h2>
                      <button onClick={() => setStep('details')} className="p-2 hover:bg-gray-100 rounded-lg">
                        <FiX className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>

                    {/* Order summary */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <div className="flex gap-4">
                        {shed.images?.[0] && (
                          <img src={shed.images[0]} alt="" className="w-16 h-16 rounded-lg object-cover" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{shed.name}</p>
                          <p className="text-sm text-gray-500">{shed.size} &middot; {shed.shedType}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">{formatPrice(effectivePrice)}</p>
                          <p className="text-xs text-gray-500">Deposit: {formatPrice(depositAmount)}</p>
                        </div>
                      </div>
                    </div>

                    <form onSubmit={handleProceedToPayment} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                          <input type="text" name="firstName" required value={customerInfo.firstName} onChange={handleInputChange}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                          <input type="text" name="lastName" required value={customerInfo.lastName} onChange={handleInputChange}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                          <input type="email" name="email" required value={customerInfo.email} onChange={handleInputChange}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                          <input type="tel" name="phone" required value={customerInfo.phone} onChange={handleInputChange}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Address</label>
                        <input type="text" name="address" value={customerInfo.address} onChange={handleInputChange}
                          placeholder="Street address for shed delivery"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                          <input type="text" name="city" value={customerInfo.city} onChange={handleInputChange}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                          <input type="text" name="state" value={customerInfo.state} onChange={handleInputChange}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                          <input type="text" name="zip" value={customerInfo.zip} onChange={handleInputChange}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                        </div>
                      </div>

                      <div className="border-2 border-emerald-600 rounded-lg p-4 text-center mt-4">
                        <p className="text-sm font-semibold text-gray-800">Questions before you pay your deposit? Give us a call:</p>
                        <a href="tel:+16822040561" className="block text-3xl font-black text-emerald-700 hover:underline whitespace-nowrap">
                          (682) 204-0561
                        </a>
                      </div>
                      <button type="submit"
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 mt-4">
                        <FiCreditCard className="w-5 h-5" />
                        Pay {formatPrice(depositAmount)} Deposit
                      </button>
                      <p className="text-xs text-center text-gray-400 mt-2">
                        Remaining {formatPrice((effectivePrice || 0) - depositAmount)} due at delivery
                      </p>
                    </form>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* SOLD popup modal */}
      <AnimatePresence>
        {showSoldModal && isSold && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowSoldModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <FiAlertCircle className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">This Shed Has Been Sold</h2>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex gap-4 bg-gray-50 rounded-lg p-4">
                  {shed.images?.[0] && (
                    <img src={shed.images[0]} alt="" className="w-20 h-20 rounded-lg object-cover" />
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">{shed.name}</p>
                    <p className="text-sm text-gray-500">{shed.size} &middot; {shed.shedType}</p>
                    <p className="text-sm text-gray-500 capitalize">{shed.bodyColor} body &middot; {shed.roofColor} roof</p>
                  </div>
                </div>

                <p className="text-gray-700 text-center text-sm">
                  Want one exactly like it? We can build an identical shed with the same specs, colors and features.
                </p>

                <Link
                  to={buildConfiguratorUrl()}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <FiBox className="w-5 h-5" />
                  Build One Just Like It
                </Link>

                <Link
                  to="/sheds"
                  className="w-full border border-gray-300 hover:border-gray-400 text-gray-700 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <FiPackage className="w-5 h-5" />
                  Browse Available Sheds
                </Link>

                <button
                  onClick={() => setShowSoldModal(false)}
                  className="w-full text-sm text-gray-400 hover:text-gray-600 py-2"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stripe Checkout Modal */}
      {showPaymentModal && (
        <StripeCheckout
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          shed={shed}
          dealer={dealer}
          paymentType="deposit"
          customerInfo={{ name: `${customerInfo.firstName} ${customerInfo.lastName}`, email: customerInfo.email, phone: customerInfo.phone }}
        />
      )}
    </>
  );
};

export default ShedCheckout;
